import { createContext, lazy, Suspense, useContext, useEffect, type ReactNode } from 'react'
import type { Verdict } from '../../preview/verdict'
import { verdictGrade } from '../../preview/verdictView'

const loadQuizRiveMascot = () => import('./QuizRiveMascot')
const QuizRiveMascot = lazy(loadQuizRiveMascot)

/**
 * Код и файлы маскота грузим заранее, как только где-то появился крупный экран
 * квиза: иначе при каждом переключении типа блока маскот сначала стоял пустым
 * местом (или статичной заглушкой в другой позе) и только потом появлялся —
 * квиз «дёргался». Браузер кеширует ответы, повторные загрузки мгновенные.
 */
let preloaded = false
const preloadQuizMascot = () => {
  if (preloaded) return
  preloaded = true
  // Модуль маскота при загрузке сам начинает разбирать свои файлы — один раз
  // на всё приложение (см. `QuizRiveMascot`).
  void loadQuizRiveMascot()
}

/** Место маскота на время загрузки — той же высоты, без заглушки в чужой позе. */
const MascotSlot = () => <div className="size-[150px] shrink-0 self-center py-1" aria-hidden />

/**
 * Живой (Rive) маскот квиза — только там, где экран крупный: холст, предпросмотр,
 * окно «Добавить блок». В миниатюрах ленты по два канваса на экран тормозили бы
 * всю ленту, поэтому по умолчанию — статичный маскот.
 */
const LiveMascotContext = createContext(false)

export const LiveQuizMascot = ({ children }: { children: ReactNode }) => {
  useEffect(preloadQuizMascot, [])
  return <LiveMascotContext.Provider value>{children}</LiveMascotContext.Provider>
}

/** Маскот для `QuizShell`: ждёт ответа, на верный — радуется (как в плеере TMA). */
export const useQuizMascot = (verdict: Verdict | null | undefined): ReactNode => {
  const live = useContext(LiveMascotContext)
  if (!live) return undefined
  return (
    <Suspense fallback={<MascotSlot />}>
      <QuizRiveMascot correct={verdictGrade(verdict ?? null) === 'correct'} />
    </Suspense>
  )
}
